import { useEffect, useState } from "react";
import { FiAlertTriangle, FiInfo } from "react-icons/fi";
import { MdScience } from "react-icons/md";
import { useParams } from "react-router-dom";
import AjudaTela from "../../../components/AjudaTela";
import BotaoRelatorio from "../../../components/relatorios/BotaoRelatorio";
import CabecalhoRelatorio from "../../../components/relatorios/CabecalhoRelatorio";
import Cartao from "../../../components/relatorios/Cartao";
import EstadoVazio from "../../../components/relatorios/EstadoVazio";
import { api } from "../../../services/api";
import { exportarRelatorioAnaliseSoloPDF } from "../../../utils/relatorioSoloPdf";

const AJUDA = {
  titulo: "Recomendação de Calagem e Adubação",
  descricao:
    "Este relatório não tem filtro nem cadastro próprio — ele é calculado em cima de uma Análise de Solo já cadastrada. Toda vez que você abre uma análise diferente em Cadastros › Análise de Solo, o relatório recalcula do zero.",
  passos: [
    {
      titulo: "1. Cadastre a amostra",
      texto: "Vá em Cadastros › Análise de Solo e lance os valores do laudo de laboratório (pH, Ca, Mg, K, Al, P, argila etc.). Só o que o laudo trouxe — campo sem dado fica em branco, nunca zero.",
    },
    {
      titulo: "2. Abra o relatório",
      texto: "Na listagem de análises, clique em \"Ver relatório\" na linha da amostra desejada. A tela atual é gerada na hora, a partir dos dados dessa amostra.",
    },
    {
      titulo: "3. Confira os alertas antes de confiar na dose",
      texto: "Se aparecer o quadro \"Alertas e Validações\", leia antes de aplicar a recomendação: pode ser inconsistência no laudo (ex.: unidade de K trocada), dado ausente que impede um cálculo, ou divergência entre os dois métodos de calagem.",
    },
    {
      titulo: "4. Sem dose para P ou K é diferente de zero",
      texto: "Se o card de Fósforo ou Potássio disser \"sem dose cadastrada\", o sistema não tem uma faixa tabelada pra essa combinação de argila/teor — não é recomendação de dose zero. Confirme com um agrônomo antes de decidir a adubação nesses casos.",
    },
    {
      titulo: "5. Exporte o PDF",
      texto: "O botão \"Exportar para PDF\" no topo gera o documento pronto pra imprimir ou enviar, com o mesmo cabeçalho (empresa/fazenda) dos outros relatórios do sistema.",
    },
  ],
  dicas: [
    "O custo estimado (calcário, P₂O₅, K₂O) só aparece se houver um item de mesmo nome cadastrado no Almoxarifado com preço — sem isso, o sistema mostra \"—\" em vez de inventar um valor.",
    "Cadastrar uma nova análise do mesmo talhão não substitui a anterior — cada amostra tem seu próprio relatório, então dá pra comparar a evolução ao longo do tempo.",
  ],
};

const formatarNumero = (valor, casas = 2) => {
  if (valor === null || valor === undefined) return "—";
  return Number(valor).toLocaleString("pt-BR", { minimumFractionDigits: casas, maximumFractionDigits: casas });
};

const formatarMoeda = (valor) => {
  if (valor === null || valor === undefined) return "—";
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(valor);
};

const SEVERIDADE_ESTILO = {
  inconsistencia: { cor: "bg-red-50 border-red-200 text-red-800", rotulo: "Inconsistência" },
  dados_ausentes: { cor: "bg-amber-50 border-amber-200 text-amber-800", rotulo: "Dados ausentes" },
  aviso: { cor: "bg-blue-50 border-blue-200 text-blue-800", rotulo: "Aviso" },
};

function LinhaParametro({ rotulo, valor, casas = 2 }) {
  return (
    <div className="flex justify-between items-baseline py-1.5 border-b border-gray-100 last:border-0">
      <span className="text-sm text-gray-500">{rotulo}</span>
      <span className="text-sm font-semibold text-gray-800">{formatarNumero(valor, casas)}</span>
    </div>
  );
}

export default function RelatorioAnaliseSolo() {
  const { id } = useParams();
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState("");

  useEffect(() => {
    buscarDados();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const buscarDados = async () => {
    setCarregando(true);
    setErro("");
    try {
      const response = await api.get(`/relatorios/agricolas/analise-solo/${id}/`);
      setDados(response.data);
    } catch (error) {
      console.error("Erro ao carregar relatório de análise de solo:", error);
      setErro(
        error.response?.data?.detail || "Erro ao gerar o relatório. Verifique se a análise foi cadastrada corretamente."
      );
    } finally {
      setCarregando(false);
    }
  };

  const exportarPDF = () => {
    if (!dados) return;
    exportarRelatorioAnaliseSoloPDF(dados);
  };

  if (carregando) {
    return <div className="min-h-screen bg-gray-50 p-8 text-center text-gray-500">Carregando...</div>;
  }

  if (erro || !dados) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 md:p-8">
        <div className="max-w-3xl mx-auto">
          <EstadoVazio titulo="Não foi possível gerar o relatório" descricao={erro} />
        </div>
      </div>
    );
  }

  const { identificacao, parametros, saturacao_elementos: satEl, relacoes, micronutrientes, calagem, potassio, fosforo, alertas, custo_estimado: custo } = dados;

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-5xl mx-auto space-y-6">
        <CabecalhoRelatorio
          icone={MdScience}
          titulo="Recomendação de Calagem e Adubação"
          descricao={`${identificacao.talhao} · ${identificacao.camada_cm}${identificacao.cultura_finalidade ? ` · ${identificacao.cultura_finalidade}` : ""}`}
          acoes={
            <>
              <AjudaTela {...AJUDA} />
              <BotaoRelatorio variante="pdf" onClick={exportarPDF}>Exportar para PDF</BotaoRelatorio>
            </>
          }
        />

        {/* Identificação */}
        <Cartao titulo="Identificação">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div><p className="text-gray-400">Propriedade</p><p className="font-medium text-gray-800">{identificacao.propriedade || "Não informada"}</p></div>
            <div><p className="text-gray-400">Talhão</p><p className="font-medium text-gray-800">{identificacao.talhao}</p></div>
            <div><p className="text-gray-400">Área</p><p className="font-medium text-gray-800">{identificacao.area_ha ? `${formatarNumero(identificacao.area_ha)} ha` : "Não informada"}</p></div>
            <div><p className="text-gray-400">Laboratório</p><p className="font-medium text-gray-800">{identificacao.laboratorio || "Não informado"}</p></div>
            <div><p className="text-gray-400">Certificado</p><p className="font-medium text-gray-800">{identificacao.certificado || "Não informado"}</p></div>
            <div><p className="text-gray-400">Data da coleta</p><p className="font-medium text-gray-800">{identificacao.data_coleta || "Não informada"}</p></div>
            <div><p className="text-gray-400">Camada analisada</p><p className="font-medium text-gray-800">{identificacao.camada_cm}</p></div>
            <div><p className="text-gray-400">Cultura/finalidade</p><p className="font-medium text-gray-800">{identificacao.cultura_finalidade || "Não informada"}</p></div>
          </div>
        </Cartao>

        {/* Alertas */}
        {alertas.length > 0 && (
          <Cartao titulo="Alertas e Validações">
            <div className="space-y-2">
              {alertas.map((a, idx) => {
                const estilo = SEVERIDADE_ESTILO[a.severidade] || SEVERIDADE_ESTILO.aviso;
                return (
                  <div key={idx} className={`flex items-start gap-2 border rounded-lg px-3 py-2 text-sm ${estilo.cor}`}>
                    {a.severidade === "inconsistencia" ? <FiAlertTriangle className="mt-0.5 shrink-0" size={16} /> : <FiInfo className="mt-0.5 shrink-0" size={16} />}
                    <div>
                      <span className="font-semibold">{estilo.rotulo}:</span> {a.mensagem}
                    </div>
                  </div>
                );
              })}
            </div>
          </Cartao>
        )}

        {/* Recomendação */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Cartao titulo="Calagem">
            <p className="text-2xl font-bold text-gray-800">
              {calagem.dispensada ? "Dispensada" : `${formatarNumero(calagem.dose_recomendada_t_ha, 1)} t/ha`}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {calagem.dispensada
                ? "Solo dentro da meta — sem necessidade de calagem."
                : "Neutralização de alumínio (dose calculada com PRNT 100%)."}
            </p>
            {calagem.divergencia_entre_metodos && (
              <p className="text-xs text-red-600 mt-2 font-medium">⚠ Divergência entre os dois métodos de cálculo — revise os dados da análise.</p>
            )}
          </Cartao>
          <Cartao titulo="Adubação Fosfatada">
            {fosforo.dose_p2o5_kg_ha != null ? (
              <>
                <p className="text-2xl font-bold text-gray-800">{formatarNumero(fosforo.dose_p2o5_kg_ha, 0)} kg/ha de P₂O₅</p>
                <p className="text-xs text-gray-500 mt-1">Classe de teor: {fosforo.classe_teor?.replace("_", " ")}.</p>
              </>
            ) : (
              <p className="text-sm text-gray-400">Sem dose cadastrada pra esta faixa de argila/teor — ver alertas.</p>
            )}
          </Cartao>
          <Cartao titulo="Adubação Potássica">
            {potassio.dose_k2o_kg_ha != null ? (
              <>
                <p className="text-2xl font-bold text-gray-800">
                  {potassio.necessaria ? `${formatarNumero(potassio.dose_k2o_kg_ha, 0)} kg/ha de K₂O` : "Não necessária"}
                </p>
                <p className="text-xs text-gray-500 mt-1">K = {formatarNumero(potassio.k_mg_dm3, 1)} mg/dm³.</p>
              </>
            ) : (
              <p className="text-sm text-gray-400">Sem K cadastrado na análise.</p>
            )}
          </Cartao>
        </div>

        {/* Parâmetros */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Cartao titulo="Parâmetros">
            <LinhaParametro rotulo="pH" valor={parametros.ph} />
            <LinhaParametro rotulo="Ca (cmolc/dm³)" valor={parametros.ca} />
            <LinhaParametro rotulo="Mg (cmolc/dm³)" valor={parametros.mg} />
            <LinhaParametro rotulo="K (cmolc/dm³)" valor={parametros.k_cmolc} casas={3} />
            <LinhaParametro rotulo="K (mg/dm³)" valor={parametros.k_mg_dm3} casas={1} />
            <LinhaParametro rotulo="Al (cmolc/dm³)" valor={parametros.al} />
            <LinhaParametro rotulo="H+Al (cmolc/dm³)" valor={parametros.h_mais_al} />
            <LinhaParametro rotulo="P (mg/dm³)" valor={parametros.p} />
            <LinhaParametro rotulo="Matéria orgânica (dag/kg)" valor={parametros.materia_organica} />
            <LinhaParametro rotulo="Argila (%)" valor={parametros.argila_pct} />
            <LinhaParametro rotulo="Soma de Bases (SB)" valor={parametros.sb} />
            <LinhaParametro rotulo="CTC efetiva (t)" valor={parametros.t_efetiva} />
            <LinhaParametro rotulo="CTC total (T)" valor={parametros.t_total} />
            <LinhaParametro rotulo="V% — Saturação por bases" valor={parametros.v_pct} casas={1} />
            <LinhaParametro rotulo="m% — Saturação por alumínio" valor={parametros.m_pct} casas={1} />
          </Cartao>

          <div className="space-y-6">
            <Cartao titulo="Saturação por elemento (% da CTC)">
              <LinhaParametro rotulo="Cálcio" valor={satEl.ca} casas={1} />
              <LinhaParametro rotulo="Magnésio" valor={satEl.mg} casas={1} />
              <LinhaParametro rotulo="Potássio" valor={satEl.k} casas={2} />
            </Cartao>
            <Cartao titulo="Relações entre bases">
              <LinhaParametro rotulo="Ca / Mg" valor={relacoes.ca_mg} />
              <LinhaParametro rotulo="Ca / K" valor={relacoes.ca_k} casas={1} />
              <LinhaParametro rotulo="Mg / K" valor={relacoes.mg_k} casas={1} />
            </Cartao>
            <Cartao titulo="Micronutrientes">
              <LinhaParametro rotulo="S (mg/dm³)" valor={micronutrientes.s} />
              <LinhaParametro rotulo="Zn (mg/dm³)" valor={micronutrientes.zn} />
              <LinhaParametro rotulo="Cu (mg/dm³)" valor={micronutrientes.cu} />
              <LinhaParametro rotulo="B (mg/dm³)" valor={micronutrientes.b} />
              <LinhaParametro rotulo="Mn (mg/dm³)" valor={micronutrientes.mn} />
              <LinhaParametro rotulo="Fe (mg/dm³)" valor={micronutrientes.fe} />
            </Cartao>
          </div>
        </div>

        {/* Custo estimado */}
        <Cartao titulo="Custo Estimado">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs text-gray-400 uppercase">
                  <th className="py-2 pr-4">Insumo</th>
                  <th className="py-2 pr-4">Dose</th>
                  <th className="py-2 pr-4">R$/ha</th>
                  <th className="py-2">Total no talhão</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { rotulo: "Calcário", linha: custo.calcario },
                  { rotulo: "Fósforo (P₂O₅)", linha: custo.fosforo },
                  { rotulo: "Potássio (K₂O)", linha: custo.potassio },
                ].map(({ rotulo, linha }) => (
                  <tr key={rotulo} className="border-b border-gray-100 last:border-0">
                    <td className="py-2 pr-4 font-medium text-gray-700">{rotulo}</td>
                    <td className="py-2 pr-4 text-gray-600">{linha.dose != null ? `${formatarNumero(linha.dose, linha.unidade === "t/ha" ? 1 : 0)} ${linha.unidade}` : "—"}</td>
                    <td className="py-2 pr-4 text-gray-600">{formatarMoeda(linha.custo_ha)}</td>
                    <td className="py-2 text-gray-600">{formatarMoeda(linha.custo_total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-gray-400 mt-3">
            Preços vêm do módulo Almoxarifado, buscados por nome do insumo — "—" quando não há item cadastrado com nome correspondente.
          </p>
        </Cartao>
      </div>
    </div>
  );
}
